/**
 * Types and utilities for dealing with email domain verification
 */

import * as zExt from "@redotech/rpc/ext";
import * as z from "zod/v4";
import { Team } from "../team";

/**
 * Status of DKIM verification for a domain
 */
export enum DkimStatus {
  PENDING = "pending",
  VERIFIED = "verified",
  FAILED = "failed",
  NOT_STARTED = "not_started",
}

/**
 * Status of Mail From domain verification
 */
export enum MailFromDomainStatus {
  PENDING = "pending",
  SUCCESS = "success",
  FAILED = "failed",
  NOT_STARTED = "not_started",
}

export const teamEmailAddressSchema = z.object({
  localPart: z.string(),
  displayName: z.string(),
  mailboxVerifiedAt: z.date().nullish(),
  domain: z.string(),
  isMailbox: z.boolean().nullish(),
  _id: zExt.objectId(),
});
export type TeamEmailAddress = z.infer<typeof teamEmailAddressSchema>;

/**
 * Email domain settings for a team
 */
export interface EmailDomainSettings {
  enabled: boolean;
  domain: string;
  mailFromDomain?: string | null;
  mailFromDomainStatus?: MailFromDomainStatus | null;
  fromName?: string | null;
  fromEmail?: string | null;
  addresses?: TeamEmailAddress[] | null;
  defaultFromAddressId?: string | null;
  defaultReplyToAddressId?: string | null;
  dkimSelector: string;
  dkimStatus: DkimStatus;
  configurationSets?: { transactional: string; marketing: string } | null;
  redoMailEnabled?: boolean | null;
  /**
   * Used for domain warming calculations, but needs to exist before warming begins
   */
  estimatedSendsPerWeek?: number | null;
  warmingEnabled?: boolean | null;
  warming?: DomainWarmingSettings | null;
}

export interface DomainWarmingSettings {
  from: {
    domain: string;
    configurationSets?: { transactional: string; marketing: string } | null;
  };
  to: {
    domain: string;
    configurationSets?: { transactional: string; marketing: string } | null;
  };
  currentTier: DomainWarmingEntry;
  history: DomainWarmingEntry[];
  startedAt: Date;
  finishedAt?: Date | null;
}

export interface DomainWarmingEntry {
  tierIndex: number;
  enteredAt: Date;
}

/**
 * Checks if an email domain is verified
 *
 * @param emailDomainSettings The email domain settings to check
 * @returns A type guard that ensures the domain is both enabled and verified
 */
export function isDkimVerifiedEmailDomain<
  T extends Pick<EmailDomainSettings, "enabled" | "dkimStatus">,
>(
  emailDomainSettings?: T,
): emailDomainSettings is T & {
  enabled: true;
  dkimStatus: DkimStatus.VERIFIED;
} {
  return (
    !!emailDomainSettings &&
    emailDomainSettings.enabled &&
    emailDomainSettings.dkimStatus === DkimStatus.VERIFIED
  );
}

export const WARMING_DOMAINS = [
  "shared1.getredomail.com",
  "shared2.getredomail.com",
  "shared3.getredomail.com",
  "shared4.getredomail.com",
] as const;

export const TRANSACTIONAL_EMAIL_DOMAIN = "notify.getredomail.com";
export const TRANSACTIONAL_EMAIL_CONFIGURATION_SET =
  "notify_getredomail_com-txnl-conf-set";
export const MARKETING_EMAIL_DOMAIN = "shared.getredomail.com";
export const MARKETING_EMAIL_CONFIGURATION_SET =
  "shared_getredomail_com-mktg-conf-set";
export const WARMING_EMAIL_CONFIGURATION_SET =
  "warming_getredomail_com-mktg-conf-set";

export function getFromEmailConfigIgnoringWarming({
  team,
  isTransactional,
}: {
  team: Pick<Team, "name"> & {
    settings?: { emailDomain?: Team["settings"]["emailDomain"] | null } | null;
  };
  isTransactional: boolean;
}): { name: string; email: string } {
  const emailDomainSettings = team?.settings?.emailDomain ?? undefined;
  if (isDkimVerifiedEmailDomain(emailDomainSettings)) {
    const defaultFromAddressId = emailDomainSettings.defaultFromAddressId;
    let address: { localPart: string; displayName?: string } | undefined =
      defaultFromAddressId
        ? emailDomainSettings.addresses?.find(
            (addr) => addr._id.toString() === defaultFromAddressId,
          )
        : undefined;
    if (!address) {
      address = {
        localPart: emailDomainSettings.fromEmail || "no-reply",
        displayName: emailDomainSettings.fromName ?? undefined,
      };
    }
    return {
      name: address.displayName || team.name || "",
      email: `${address.localPart}@${emailDomainSettings.domain}`,
    };
  } else if (emailDomainSettings && emailDomainSettings.redoMailEnabled) {
    const emailDomain = isTransactional
      ? TRANSACTIONAL_EMAIL_DOMAIN
      : MARKETING_EMAIL_DOMAIN;
    return { name: team.name ?? "Redo", email: `no-reply@${emailDomain}` };
  } else {
    return { name: team.name ?? "Redo", email: "no-reply@getredo.com" };
  }
}
